"use client";

import React from "react";
import Image from "next/image";
import { AppleIcon, ContactsIcon, ControlCenterIcon, SearchIcon, WifiIcon } from "./mac-icons";
import { RiContactsBook2Fill, RiQuillPenAiFill, RiFlagFill, RiLineChartFill } from "@remixicon/react";
import { AdquisitionChannelTabs } from "./adquisition-channel-tabs";
import { useTranslations } from "next-intl";

type FeatureConfig = {
    key: "import" | "build" | "start" | "track";
    label: string;
    Icon: React.ComponentType<{ className?: string }>;
    image: string;
    mobileWindowClass?: string;
    durationMs: number;
};

const FEATURE_CONFIG: FeatureConfig[] = [
    {
        key: "import",
        label: "home.agent.features.labels.import",
        Icon: RiContactsBook2Fill,
        image: "/features/import-leads.jpg",
        //mobileWindowClass: "w-[96%] h-[95%] bottom-0 right-0",
        durationMs: 3500,
    },
    {
        key: "build",
        label: "home.agent.features.labels.build",
        Icon: RiQuillPenAiFill,
        image: "/features/flow.jpg",
        //mobileWindowClass: "w-[94%] h-[92%] bottom-1 right-1",
        durationMs: 4200,
    },
    {
        key: "start",
        label: "home.agent.features.labels.start",
        Icon: RiFlagFill,
        image: "/features/start-campaign.jpg",
       // mobileWindowClass: "w-[92%] h-[88%] bottom-2 right-2",
        durationMs: 3800,
    },
    {
        key: "track",
        label: "home.agent.features.labels.track",
        Icon: RiLineChartFill,
        image: "/features/metrics.jpg",
        //mobileWindowClass: "w-[90%] h-[86%] bottom-2 right-2",
        durationMs: 4000,
    },
];

const formatMenuBarTime = (date: Date): string => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];
    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    const dayOfMonth = date.getDate();
    let hours = date.getHours();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    if (hours === 0) hours = 12;
    const hourStr = hours < 10 ? `0${hours}` : `${hours}`;
    const minutes = date.getMinutes();
    const minuteStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
    return `${dayName} ${monthName} ${dayOfMonth} ${hourStr}:${minuteStr} ${ampm}`;
};



export default function FeaturesSlide() {
    const t = useTranslations('home.agent.features');
    const [menuBarTime, setMenuBarTime] = React.useState<string>("");
    const [activeKey, setActiveKey] = React.useState<FeatureConfig["key"]>("build");
    const [channel, setChannel] = React.useState<'inbound' | 'outbound'>("outbound");
    const mobileTabsRef = React.useRef<HTMLDivElement | null>(null);
    const mobileButtonRefs = React.useRef<Record<FeatureConfig["key"], HTMLButtonElement | null>>({
        import: null,
        build: null,
        start: null,
        track: null,
    });

    const visibleFeatures = React.useMemo(() => {
        return channel === 'inbound'
            ? FEATURE_CONFIG.filter((f) => f.key !== 'import')
            : FEATURE_CONFIG;
    }, [channel]);

    React.useEffect(() => {
        const updateTime = () => {
            setMenuBarTime(formatMenuBarTime(new Date()));
        };
        updateTime();
        const intervalId = setInterval(updateTime, 30000);
        return () => clearInterval(intervalId);
    }, []);

    // Ensure activeKey is valid when the visible features change (e.g., inbound hides "Import Leads")
    React.useEffect(() => {
        if (!visibleFeatures.some((f) => f.key === activeKey)) {
            const fallback = visibleFeatures[0];
            if (fallback) setActiveKey(fallback.key);
        }
    }, [visibleFeatures, activeKey]);

    // Auto-rotate features based on per-feature duration within the visible set
    React.useEffect(() => {
        if (visibleFeatures.length === 0) return;
        const defaultFeature = visibleFeatures[0]!;
        const currentIndex = visibleFeatures.findIndex((f) => f.key === activeKey);
        const current = visibleFeatures[currentIndex] ?? defaultFeature;
        const timer = setTimeout(() => {
            const baseIndex = currentIndex >= 0 ? currentIndex : 0;
            const nextIndex = (baseIndex + 1) % visibleFeatures.length;
            const next = visibleFeatures[nextIndex] ?? defaultFeature;
            setActiveKey(next.key);
        }, current.durationMs);
        return () => clearTimeout(timer);
    }, [activeKey, visibleFeatures]);

    // Smoothly center the active mobile tab in view when it changes
    const scrollActiveMobileTabIntoView = React.useCallback(() => {
        const container = mobileTabsRef.current;
        const btn = mobileButtonRefs.current[activeKey];
        if (!container || !btn) return;
        // Only run on < xl viewports to avoid unnecessary work
        if (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 1280px)").matches) {
            return;
        }
        const containerRect = container.getBoundingClientRect();
        const btnRect = btn.getBoundingClientRect();
        const currentScrollLeft = container.scrollLeft;
        const relativeBtnLeft = btnRect.left - containerRect.left; // within container viewport
        const targetLeft = currentScrollLeft + relativeBtnLeft - (container.clientWidth / 2 - btnRect.width / 2);
        container.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
    }, [activeKey]);

    React.useEffect(() => {
        scrollActiveMobileTabIntoView();
    }, [activeKey, scrollActiveMobileTabIntoView]);


    return (
        <section className="second-screen pt-12 md:pt-16 lg:pt-24">
            <div className="w-full mx-auto max-w-7xl px-5 md:px-8">
                <span className="block text-18 leading-snug font-medium tracking-tight text-theos-500 lg:text-24">
                    {t('kicker')}
                </span>
                <h2 className="mt-[10px] max-w-none text-[24px] leading-tight font-medium tracking-4 md:mt-[14px] md:max-w-[540px] md:text-[28px] lg:mt-[18px] lg:max-w-[700px] lg:text-[36px] xl:max-w-[770px] xl:text-[40px]">
                    {t('title')}
                </h2>
                <div className="mt-4">
                    <AdquisitionChannelTabs onTabChange={setChannel} />
                </div>
                <div className="mt-8 hidden transition-opacity md:mt-14 lg:mt-[72px] xl:block opacity-100">
                    <div className="-my-1 flex w-[calc(100%+40px)] items-center gap-x-[13px] py-1 text-15 lg:gap-x-[19px] lg:text-base">
                        {visibleFeatures.map((f, idx) => (
                            <React.Fragment key={f.key}>
                                <button
                                    onClick={() => setActiveKey(f.key)}
                                    className={`flex shrink-0 cursor-pointer items-center gap-x-2 rounded transition-colors duration-200 ${activeKey === f.key ? "text-theos-500" : "text-gray-500 hover:text-[#6684FF]"
                                        }`}
                                >
                                    <f.Icon className="size-[18px]" />
                                    <span className="shrink-0 leading-snug font-medium tracking-tight">
                                        {t(`labels.${f.key}`)}
                                    </span>
                                </button>
                                {idx < visibleFeatures.length - 1 && (
                                    <div className="h-[18px] w-px shrink-0 bg-gray-500 opacity-10">
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
                <div className="relative hidden aspect-[1216/620] w-full overflow-hidden rounded-3xl px-6 py-5 xl:mt-[32px] xl:block">
                    {/* <Image
                    src="/funnel/desktop-wallpaper.jpg"
                    alt="desktop wallpaper"
                    width={2432}
                    height={1240}
                    className="w-full h-full object-cover"
                /> */}
                    <Image
                        src="/funnel/desktop-wallpaper2.jpg"
                        alt="desktop-wallpaper"
                        fill
                        loading="lazy"
                        className="absolute inset-0 h-full w-full object-cover"
                        sizes="(max-width: 768px) 100vw, 1216px"
                        quality={100}
                    />
                    <div className="pointer-events-none absolute -top-3.5 left-0 h-px w-full">
                    </div>
                    <div className="menu-bar absolute inset-x-0 top-0 z-0 bg-white/40 text-[11px]">
                        <div className="flex items-center justify-between gap-x-2 px-4 font-apple">

                            <div className="flex items-center gap-x-[9px]">
                                <AppleIcon />
                                <div className="px-[4px] py-[3px] leading-tight tracking-tight text-black font-bold">
                                    Finder
                                </div>
                                <div className="px-[4px] py-[3px] leading-tight font-semibold tracking-tight text-black">
                                    File
                                </div>
                                <div className="px-[4px] py-[3px] leading-tight font-semibold tracking-tight text-black">
                                    Edit
                                </div>
                                <div className="px-[4px] py-[3px] leading-tight font-semibold tracking-tight text-black">
                                    View
                                </div>
                                <div className="px-[4px] py-[3px] leading-tight font-semibold tracking-tight text-black">
                                    Go
                                </div>
                                <div className="px-[4px] py-[3px] leading-tight font-semibold tracking-tight text-black">
                                    Window
                                </div>
                                <div className="px-[4px] py-[3px] leading-tight font-semibold tracking-tight text-black">
                                    Help
                                </div>
                            </div>

                            <div className="flex items-center gap-x-4">
                                <WifiIcon />
                                <SearchIcon />
                                <ContactsIcon />
                                <ControlCenterIcon />
                                <span className="text-10 leading-tight font-semibold tracking-tight text-black">
                                    {menuBarTime ? menuBarTime : "Tue Sep 9 02:02 PM"}
                                </span>
                            </div>
                        </div>
                    </div>
                    {visibleFeatures.map((f) => (
                        <div
                            key={f.key}
                            className={`absolute top-1/2 left-1/2 h-[530px] w-[1096px] -translate-x-1/2 -translate-y-1/2 flex flex-col justify-between overflow-hidden rounded-[8px] bg-gray-100/80 shadow-[0_5px_10px_0_rgba(0,0,0,0.20)] transition-opacity duration-300 ${activeKey === f.key ? "opacity-100" : "opacity-0 pointer-events-none"
                                }`}
                            aria-hidden={activeKey !== f.key}
                        >
                            <div className="flex h-6 w-full items-center gap-x-1 bg-gray-20 px-[6px]">
                                <div className="size-2.5 rounded-full border border-gray-1000/10 bg-gray-200">
                                </div>
                                <div className="size-2.5 rounded-full border border-gray-1000/10 bg-gray-200">
                                </div>
                                <div className="size-2.5 rounded-full border border-gray-1000/10 bg-gray-200">
                                </div>
                            </div>
                            <div className="relative h-[calc(100%-24px)] bg-background">
                                <Image
                                    src={f.image}
                                    alt={t(`labels.${f.key}`)}
                                    fill
                                    className="object-contain"
                                    sizes="(max-width: 768px) 100vw, 1030px"
                                    quality={100}
                                />
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-8 block md:mt-14 lg:mt-[72px] xl:hidden">
                    <div
                        ref={mobileTabsRef}
                        role="tablist"
                        aria-orientation="horizontal"
                        className="scrollbar-hidden -mx-5 flex w-[calc(100%+40px)] items-center gap-x-[13px] overflow-x-auto overflow-y-hidden pr-5 text-15 lg:gap-x-[19px] lg:text-base"
                    >
                        {visibleFeatures.map((f, idx) => (
                            <React.Fragment key={f.key}>
                                <button
                                    ref={(el) => { mobileButtonRefs.current[f.key] = el; }}
                                    onClick={() => setActiveKey(f.key)}
                                    className={`flex shrink-0 cursor-pointer items-center gap-x-2 rounded transition-colors duration-200 first:ml-5 ${activeKey === f.key ? "text-theos-500" : "text-gray-500 hover:text-[#6684FF]"
                                        }`}
                                    role="tab"
                                    aria-selected={activeKey === f.key}
                                    tabIndex={activeKey === f.key ? 0 : -1}
                                >
                                    <f.Icon className="size-[18px]" />
                                    <span className="shrink-0 leading-snug font-medium tracking-tight">
                                    {t(`labels.${f.key}`)}
                                    </span>
                                </button>
                                {idx < visibleFeatures.length - 1 && (
                                    <div className="h-[18px] w-px shrink-0 bg-gray-500 opacity-10" aria-hidden="true">
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="relative mt-5 aspect-[1216/620] w-full md:mt-[30px] xl:mt-8 rounded-3xl overflow-hidden">
                        <Image
                            src="/funnel/desktop-wallpaper2.jpg"
                            alt="desktop-wallpaper"
                            fill
                            loading="lazy"
                            className="absolute inset-0 h-full w-full object-cover"
                            sizes="(max-width: 768px) 100vw, 1216px"
                            quality={100}
                        />
                        {visibleFeatures.map((f) => (
                            <div
                                key={f.key}
                                className={`absolute ${f.mobileWindowClass || "w-[96%] h-[95%] -translate-x-1/2 left-1/2 -translate-y-1/2 top-1/2"} flex-col justify-between overflow-hidden rounded-[8px] bg-gray-100/80 transition-opacity duration-300 ${activeKey === f.key ? "opacity-100" : "opacity-0 pointer-events-none"
                                    }`}
                                aria-hidden={activeKey !== f.key}
                            >
                                <div className="flex md:h-6 w-full items-center md:gap-x-1 bg-gray-20 px-[6px] h-2 gap-x-0.5">
                                    <div className="md:size-2.5 size-1 rounded-full border border-gray-1000/10 bg-gray-200">
                                    </div>
                                    <div className="md:size-2.5 size-1 rounded-full border border-gray-1000/10 bg-gray-200">
                                    </div>
                                    <div className="md:size-2.5 size-1 rounded-full border border-gray-1000/10 bg-gray-200">
                                    </div>
                                </div>
                                <div className="relative h-[calc(100%-24px)] bg-background">
                                    <Image
                                        src={f.image}
                                        alt={t(`labels.${f.key}`)}
                                        fill
                                        className="object-contain"
                                        sizes="(max-width: 768px) 100vw, 1030px"
                                        quality={100}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}